import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { BankingCashflowProps, ReportData } from '../tokens/types';
import { PdfSection } from '../primitives/PdfPrimitives';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

const formatCell = (v?: string) => (v == null || v === '' ? '—' : v);

const valueColumns = (b: BankingCashflowProps, max = 12) => b.headers.filter((h) => h.key !== 'name').slice(-max);

const PdfBankingCashflow: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const b = data.bankingCashflow;
    if (!b || b.rows.length === 0) return null;
    const cols = valueColumns(b, 6);
    return (
        <PdfSection
            kicker="Section · Banking"
            title="Cashflow from Banking"
            subtitle="AI-generated cashflow statement derived from bank statement classifications"
            index={index}
            breakBefore={breakBefore}
        >
            <View style={{ marginTop: space.sm, borderTopWidth: 0.5, borderTopColor: colors.ink }}>
                <View
                    style={{
                        flexDirection: 'row',
                        paddingVertical: 5,
                        borderBottomWidth: 0.5,
                        borderBottomColor: colors.ink,
                        backgroundColor: colors.surfaceAlt,
                    }}
                    fixed
                >
                    <Text
                        style={{
                            width: '32%',
                            fontSize: typeScale.micro,
                            fontFamily: fontFamily.sansBold,
                            color: colors.muted,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            paddingHorizontal: 4,
                        }}
                    >
                        Cashflow head
                    </Text>
                    {cols.map((h) => (
                        <Text
                            key={h.key}
                            style={{
                                flex: 1,
                                fontSize: typeScale.micro,
                                fontFamily: fontFamily.sansBold,
                                color: colors.muted,
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                                textAlign: 'right',
                                paddingHorizontal: 4,
                            }}
                        >
                            {h.name}
                        </Text>
                    ))}
                </View>
                {b.rows.map((r, i) => (
                    <View
                        key={i}
                        style={{
                            flexDirection: 'row',
                            paddingVertical: 4,
                            borderBottomWidth: r.isSummary ? 0.75 : 0.25,
                            borderBottomColor: r.isSummary ? colors.ink : colors.hairlineSoft,
                            backgroundColor: r.isSummary ? colors.surfaceAlt : undefined,
                        }}
                        wrap={false}
                    >
                        <Text
                            style={{
                                width: '32%',
                                fontSize: typeScale.caption,
                                color: colors.inkSoft,
                                fontFamily: r.isSummary || r.isAggregate ? fontFamily.sansBold : fontFamily.sans,
                                paddingHorizontal: 4,
                            }}
                        >
                            {r.name}
                        </Text>
                        {cols.map((h) => (
                            <Text
                                key={h.key}
                                style={{
                                    flex: 1,
                                    fontSize: typeScale.caption,
                                    color: colors.ink,
                                    fontFamily: fontFamily.sans,
                                    textAlign: 'right',
                                    paddingHorizontal: 4,
                                }}
                            >
                                {formatCell(r.values[h.name] ?? r.values[h.key])}
                            </Text>
                        ))}
                    </View>
                ))}
            </View>
        </PdfSection>
    );
};

const ScreenBankingCashflow: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const b = data.bankingCashflow;
    if (!b || b.rows.length === 0) return null;
    const cols = valueColumns(b, 24);
    const thStyle: React.CSSProperties = {
        padding: '8px 8px',
        fontSize: 10,
        color: colors.muted,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        textAlign: 'right',
        borderBottom: `1px solid ${colors.ink}`,
        whiteSpace: 'nowrap',
        background: colors.surface,
    };
    const tdStyle: React.CSSProperties = {
        padding: '6px 8px',
        fontSize: 11.5,
        textAlign: 'right',
        color: colors.ink,
        borderBottom: `0.5px solid ${colors.hairlineSoft}`,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    };
    const stickyHeadStyle: React.CSSProperties = {
        ...thStyle,
        textAlign: 'left',
        position: 'sticky',
        left: 0,
        zIndex: 2,
        minWidth: 180,
        background: colors.surface,
    };
    const stickyCellStyle: React.CSSProperties = {
        ...tdStyle,
        textAlign: 'left',
        position: 'sticky',
        left: 0,
        zIndex: 1,
        minWidth: 180,
    };
    return (
        <ScreenSection
            kicker="Section · Banking"
            title="Cashflow from Banking"
            subtitle="AI-generated cashflow statement derived from bank statement classifications"
            index={index}
        >
            <div style={{ overflowX: 'auto', border: `1px solid ${colors.hairlineSoft}`, borderRadius: 6, marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            <th style={stickyHeadStyle}>Cashflow head</th>
                            {cols.map((h) => (
                                <th key={h.key} style={thStyle}>
                                    {h.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {b.rows.map((r, i) => {
                            const rowBg = r.isSummary ? colors.surfaceAlt : i % 2 === 0 ? '#FFFFFF' : colors.surface;
                            return (
                                <tr
                                    key={i}
                                    style={{
                                        borderTop: r.isSummary ? `1px solid ${colors.ink}` : undefined,
                                    }}
                                >
                                    <td
                                        style={{
                                            ...stickyCellStyle,
                                            color: colors.inkSoft,
                                            fontWeight: r.isSummary || r.isAggregate ? 700 : 400,
                                            background: rowBg,
                                        }}
                                    >
                                        {r.name}
                                    </td>
                                    {cols.map((h) => (
                                        <td key={h.key} style={{ ...tdStyle, background: rowBg }}>
                                            {formatCell(r.values[h.name] ?? r.values[h.key])}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </ScreenSection>
    );
};

export const bankingCashflowModule: ModuleDefinition = {
    meta: {
        id: 'bankingCashflow' as any,
        title: 'Cashflow from banking',
        description: 'Operating, financing and investing cashflows derived from bank statements.',
        dataSource: 'polus/data/bankStatementPivotData',
    },
    hasData: (d) => !!d.bankingCashflow && d.bankingCashflow.rows.length > 0,
    Pdf: PdfBankingCashflow,
    Screen: ScreenBankingCashflow,
};
